// @ts-nocheck
import { isValidElement } from "react";
import { useMemo } from "react";
import { Item } from "components/src/components/Item/Item";
import cx from "components/src/utils/cx";
import type { NavItemProps } from "./types";
import type { BadgeSizeType } from "components/src/components/Badge/types";
import type { SizeType } from "components/src/shared/types";
import { Icon } from "components/src/components/Icon/Icon";
import { cssColor } from "shared/src/utils/cssUtils";

import styles from "../../../scss/NavItem.module.scss";

const navItemSizeConfig = {
  xs: {
    iconSize: "sm" as SizeType,
    textSize: "heading-sm",
    width: "cui-min-w-[20px]",
    padding: "cui-py-2xs cui-px-xs",
    badgeSize: "xs" as BadgeSizeType,
  },
  sm: {
    iconSize: "md" as SizeType,
    textSize: "heading-sm",
    width: "cui-min-w-[32px]",
    padding: "cui-p-xs",
    badgeSize: "xs" as BadgeSizeType,
  },
  md: {
    iconSize: "lg" as SizeType,
    textSize: "heading-md",
    width: "cui-min-w-[44px]",
    padding: "cui-p-sm",
    badgeSize: "sm" as BadgeSizeType,
  },
  lg: {
    iconSize: "xl" as SizeType,
    textSize: "heading-lg",
    width: "cui-min-w-[56px]",
    padding: "cui-p-md",
    badgeSize: "sm" as BadgeSizeType,
  },
};

const navItemFocusClasses =
  "focus-visible:cui-outline-1px focus-visible:cui-outline-offset-1px focus-visible:cui-outline-fg-focus";

const navItemFontColor = {
  base: {
    tab: {
      default: "cui-text-fg-default",
      minimal:
        "cui-text-fg-moderate group-hover/navitem:cui-text-fg-default active:cui-text-fg-default aria-selected:cui-text-fg-default",
    },
    pill: {
      default:
        "cui-text-fg-default group-hover/navitem:cui-text-fg-default active:cui-text-fg-default aria-disabled:cui-text-fg-subtle",
      minimal:
        "cui-text-fg-default group-hover/navitem:cui-text-fg-default active:cui-text-fg-default aria-disabled:cui-text-fg-subtle",
    },
  },
  selected: {
    tab: {
      default: "cui-text-fg-default",
      minimal: "cui-text-fg-default",
    },
    pill: {
      default: "cui-text-fg-default",
      minimal: "cui-text-fg-primary",
    },
  },
  disabled: {
    tab: {
      default: "cui-text-fg-subtle",
      minimal: "cui-text-fg-subtle",
    },
    pill: {
      default: "cui-text-fg-subtle",
      minimal: "cui-text-fg-subtle",
    },
  },
};

/**
 * TODO: Finish
 */

const navItemBgColors = {
  tab: {
    default: `${styles["nav-item"]} aria-disabled:cui-bg-bg-layer-one`,
    minimal:
      "active:cui-bg-neutrals-opacity-30 aria-selected:cui-bg-bg-layer-two",
  },
  pill: {
    default:
      "cui-bg-bg-layer-two hover:cui-bg-bg-layer-one active:cui-bg-bg-layer-two aria-selected:cui-bg-bg-layer-three aria-disabled:cui-bg-bg-layer-two",
    minimal:
      "cui-bg-bg-default hover:cui-bg-bg-layer-one active:cui-bg-bg-layer-two aria-selected:cui-bg-bg-layer-two",
  },
};

export const NavItemDefault = ({
  id,
  type,
  label,
  value,
  variant = "tab",
  disabled = false,
  leadingIcon,
  trailingIcon,
  badge,
  active,
  size = "md",
  className = "",
  mode = "light",
  animation = true,
  itemsObjByIdMap,
  itemsObjByIndexMap,
  boldText,
  bgColor,
  onClick,
  elementTimingLeading,
  elementTimingTrailing,
}: NavItemProps) => {
  const selected =
    !disabled &&
    (active == id ||
      itemsObjByIdMap[active] === id ||
      itemsObjByIndexMap[active] === id);

  const iconProps = useMemo(
    () => ({
      mode,
      color: disabled ? "fg-subtle" : "fg-default",
      size: navItemSizeConfig[size].iconSize,
    }),
    [leadingIcon, trailingIcon, mode, selected, variant, disabled, size]
  );

  // Resolve icons, if passed in a string.  If you pass in a ReactNode, just render that.
  // const _elementTimingLeading = elementTimingLeading ? { elementtiming: elementTimingLeading } : undefined;
  // const _elementTimingTrailing = elementTimingTrailing ? { elementtiming: elementTimingTrailing } : undefined;
  const [leadingEnd, trailingStart] = [
    leadingIcon && typeof leadingIcon === "string"
      ? Icon({ name: leadingIcon, elementtiming: `featured_nav`, ...iconProps })
      : isValidElement(leadingIcon)
        ? leadingIcon
        : null,
    trailingIcon && typeof trailingIcon === "string"
      ? Icon({
          name: trailingIcon,
          elementtiming: `featured_nav`,
          ...iconProps,
        })
      : isValidElement(trailingIcon)
        ? trailingIcon
        : null,
  ];

  const itemConfig = useMemo(() => {
    const hasBadge = !!badge;
    const isStrBadge = typeof badge === "string";
    let _trailingStart = trailingIcon;

    if (hasBadge) {
      let _badgeProps = {
        text: isStrBadge ? badge : badge?.text,
        as: "badge",
        shape: "round",
        size: navItemSizeConfig[size].badgeSize
      };
      typeof badge === "object" && (_badgeProps = { ..._badgeProps, ...badge });
      _trailingStart = <Item.Slot {..._badgeProps} />;
      console.log({_trailingStart})
    }

    return {
      leadingEnd,
      trailingStart: _trailingStart,
      trailingEnd: hasBadge ? trailingIcon : null,
    };
  }, [leadingEnd, trailingStart, badge, size, mode, selected]);

  const navItemBeforeClasses = useMemo(() => {
    const hasLeadingIcon = !!leadingIcon;
    return `before:cui-content-[attr(data-label)] before:cui-absolute before:cui-top-1/2 before:-cui-translate-y-[calc(50%+1px)] ${
      hasLeadingIcon
        ? "before:cui-left-8"
        : "before:cui-left-1/2 before:-cui-translate-x-1/2"
    }`.trim();
  }, [leadingIcon, size]);

  const navItemBorderClasses = useMemo(() => {
    const minimalRoundedMap = {
      xs: "cui-rounded-2",
      sm: "cui-rounded-md",
      md: "cui-rounded-md",
      lg: "cui-rounded-[10px]",
    };
    const _minimalRounded =
      size && size in minimalRoundedMap
        ? minimalRoundedMap[size]
        : minimalRoundedMap["md"];

    return {
      tab: {
        default:
          "cui-border-solid cui-border-0 cui-border-b-2 cui-border-b-neutrals-transparent hover:cui-border-b-fg-subtle active:cui-border-b-fg-subtle aria-selected:cui-border-b-fg-primary-strong",
        minimal: "cui-rounded-t-md cui-rounded-b-0",
      },
      pill: {
        default:
          "cui__nav-item-pill cui-rounded-md cui-border-solid cui-border-0 cui-border-1 cui-border-neutrals-transparent hover:cui-border-fg-primary-minimal aria-selected:cui-border-fg-primary aria-disabled:cui-border-neutrals-transparent",
        minimal: _minimalRounded,
      },
    };
  }, [size]);

  const _fontColor = useMemo(() => {
    return navItemFontColor[
      disabled ? "disabled" : selected ? "selected" : "base"
    ][variant][type];
  }, [variant, disabled, selected, type]);

  const _navItemClasses = useMemo(() => {
    return cx(
      [
        "cui-group/navitem",
        `${navItemSizeConfig[size].textSize}${
          selected && variant === "tab" ? "-bold" : ""
        }`,
        navItemSizeConfig[size].padding,
        navItemBorderClasses[variant][type],
        navItemBgColors[variant][type],
        navItemSizeConfig[size].width,
        navItemFocusClasses,
        className,
        disabled ? "cui-pointer-events-none" : "",
      ],
      `cui-cursor-pointer cui-select-none cui-whitespace-nowrap cui-relative`
    );
  }, [type, variant, size, className, disabled]);

  const navItemGap = useMemo(() => {
    if (variant === "pill" || variant === "tab") {
      const gapSize = (size === "xs" || size === "sm") ? "2xs" : "sm";
      // Override ItemRow's hardcoded cui-gap-xs
      return `!cui-gap-x-${gapSize === "2xs" ? "2xs" : "sm"}`;
    }
    return "";
  }, [variant, size]);

  return (
    <Item.Row
      {...itemConfig}
      id={id}
      disabled={!!disabled}
      aria-disabled={!!disabled}
      aria-current={!!selected}
      aria-selected={!!selected}
      textColor={_fontColor}
      className={cx(_navItemClasses, navItemGap)}
      colSpacing={null}
      data-label={label}
      tabIndex={disabled ? -1 : 0}
      onClick={onClick}
      role="tab"
      style={{
        background: cssColor(bgColor),
        minHeight: 20
      }}
    >
      <span
        className={`cui-w-full cui-text-center ${size === 'lg' ? 'cui-min-h-[24px] cui-flex cui-items-center cui-justify-center' : ''} ${
          navItemSizeConfig[size].textSize
        }${boldText ? "-bold" : ""}`}
        elementtiming={`featured_nav`}
      >
        {label}
      </span>
    </Item.Row>
  );
};
